import { useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

import type { ConsultaDeMunicipio } from '@/components/busca/textoDaBusca'

/** Município como a lista de sugestões o devolve: `rotulo` já vem como "Nome-UF". */
export interface MunicipioSugerido {
  codigo: number
  nome: string
  uf: string
  rotulo: string
  coberto: boolean
}

/** Quantas sugestões a lista mostra; a API devolve até 10. */
export const MAXIMO_DE_SUGESTOES = 6

/** O município pelo código, como `GET /api/v1/municipios/{codigo}` o devolve. */
export interface MunicipioDoCodigo {
  codigo: number
  nome: string
  ufSigla: string
  coberto: boolean
  bbox: [number, number, number, number]
}

const UM_MINUTO = 60_000

/** Chave e função da consulta de sugestões; o Enter a usa direto, sem esperar a pausa. */
export function consultaDeSugestoes(consulta: ConsultaDeMunicipio) {
  const parametros = new URLSearchParams({ texto: consulta.texto })
  if (consulta.uf !== undefined) parametros.set('uf', consulta.uf)
  return {
    queryKey: ['municipios', consulta.texto, consulta.uf ?? null] as const,
    queryFn: () => apiFetch<MunicipioSugerido[]>(`/v1/municipios?${parametros.toString()}`),
    staleTime: UM_MINUTO,
  }
}

/**
 * As sugestões de município para a consulta, ou nada enquanto `consulta` for
 * `null`. Sem repetir pedido que falhou: a lista mostra o erro na hora. A chave
 * leva o texto, então resposta de um texto antigo nunca vira a lista do atual.
 */
export function useSugestoes(consulta: ConsultaDeMunicipio | null) {
  return useQuery({
    ...consultaDeSugestoes(consulta ?? { texto: '' }),
    enabled: consulta !== null,
    retry: false,
  })
}

/** O município de um `?municipio=` da URL, para o campo mostrar o rótulo dele. */
export function useMunicipioDoCodigo(codigo: number | undefined) {
  return useQuery({
    queryKey: ['municipio', codigo ?? null] as const,
    queryFn: () => apiFetch<MunicipioDoCodigo>(`/v1/municipios/${codigo}`),
    enabled: codigo !== undefined,
    staleTime: UM_MINUTO,
  })
}
